import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@/store/types";
import debounce from "lodash/debounce";

// Selectors
const selectActiveBuildingCode = (state: RootState) => state.authInfo.activeBuildingCode;
const selectFeatureFlags = (state: RootState) => state.featureFlags;
const selectMapApiKeys = (state: RootState) => state.authInfo.mapApiKeys;

import { fetchAllFeatureFlags } from "@/features/yard360/store/featureFlags/actions";
import { fetchPermissions } from "@/features/yard360/store/permissions/actions";
import { saveAuthInfo } from "@/features/yard360/store/authInfo/actions";
import { parseWithoutThrowingException } from "@/utils/jsonUtils";
import rumService, { RUM_EVENT_TYPES } from "@/utils/awsRUM/awsRUM";
import { FEATURE_FLAGS, ROUTE_TO_VIEW, VIEW_TO_ROUTE, YARD_FEATURES } from "@/app/app-constants";
import { keys } from "@/i18n/keys";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { Pendo } from "@/services/pendo/Pendo";

// Add this at the top of your file or in a separate types file (e.g., types/rum.d.ts)
declare global {
  interface Window {
    cwr?: {
      plugins?: any[];
      recordEvent?: (eventName: string, eventData?: any) => void;
      getSession?: () => any;
      recordError?: (error: Error) => void;
    };
    pendo?: {
      initialize: (options: any) => void;
      updateOptions: (options: any) => void;
      track: (eventName: string, properties?: any) => void;
    };
  }
}

interface AuthInfo {
  securityToken: string;
  realm: string;
  domain: string;
  activeAccountName?: string;
  activeBuildingCode?: string;
  mapApiKeys?: {
    [mapApiKey: string]: string;
  };
  context?: TokenContext["context"];
}

// Extend the JwtPayload interface
interface TokenContext extends JwtPayload {
  context?: {
    accountId?: string;
    userId?: string;
    userName?: string;
    userType?: string;
    yard?: string;
  };
}

export const useAppInitialization = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const prevSearchParamsRef = useRef(location.search);
  const prevHashRef = useRef(location.hash);
  const prevPathRef = useRef(location.pathname);
  const isInitializedRef = useRef(false);
  const pendoInitializedRef = useRef(false);

  const [currentTab, setCurrentTab] = useState(
    ROUTE_TO_VIEW[location.pathname] || keys.APPOINTMENTS
  );

  const [error, setError] = useState<string | null>(null);
  const [isFeatureFlagsLoading, setIsFeatureFlagsLoading] = useState(true);
  const activeBuildingCode = useSelector(selectActiveBuildingCode);
  const featureFlags = useSelector(selectFeatureFlags);
  const mapApiKeys = useSelector(selectMapApiKeys);
  const parseMapApiKeys = (rawKeys: string): AuthInfo["mapApiKeys"] => {
    try {
      // Guard against undefined or null
      if (!rawKeys) {
        console.warn("Map API keys string is empty or undefined");
        return {};
      }

      // Replace single quotes with double quotes
      const sanitizedJson = rawKeys.replace(/'/g, '"');

      // Parse JSON with type assertion
      const parsed = JSON.parse(sanitizedJson) as any;

      // Validate the parsed object
      if (typeof parsed !== "object" || parsed === null) {
        console.warn("Parsed map API keys is not an object");
        return {};
      }

      return parsed;
    } catch (error) {
      console.error("Error parsing map API keys:", error);
      return {};
    }
  };

  // Function to update Pendo with current screen dimensions
  const updatePendoScreenSize = () => {
    if (!pendoInitializedRef.current || !window.pendo) return;

    // Get current screen dimensions
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const screenResolution = `${screenWidth}x${screenHeight}`;

    // Track as a custom event
    window.pendo.track("screen_size_updated", {
      width: screenWidth,
      height: screenHeight,
      screenResolution,
    });
  };

  const debouncedUpdatePendoScreenSize = debounce(updatePendoScreenSize, 300);

  useEffect(() => {
    if (pendoInitializedRef.current) {
      window.addEventListener("resize", debouncedUpdatePendoScreenSize);
      return () => {
        window.removeEventListener("resize", debouncedUpdatePendoScreenSize);
      };
    }
  }, [debouncedUpdatePendoScreenSize, pendoInitializedRef.current]);

  // Handle authentication message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === "Component mounted") return;

      try {
        const json = typeof event.data === "string" && parseWithoutThrowingException(event.data);

        if (json?.securityToken && !isInitializedRef.current) {
          const decoded = jwtDecode<TokenContext>(json.securityToken);
          const mapApiKeysObject = parseMapApiKeys(json?.mapApiKeys ?? "");

          const authInfo: AuthInfo = {
            securityToken: json.securityToken,
            realm: json.realm,
            domain: json.domain,
            activeBuildingCode: json.activeAccountName,
            mapApiKeys: mapApiKeysObject,
            context: decoded?.context,
          };

          dispatch(saveAuthInfo(authInfo));

          rumService.init({
            domain: json.domain,
            realm: json.realm,
          });

          try {
            const alias = decoded?.context?.userName?.split("@")[0] || "";

            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            const screenResolution = `${screenWidth}x${screenHeight}`;

            // Wrap Pendo initialization in a Promise to catch any errors
            Promise.resolve()
              .then(() => {
                try {
                  new Pendo().initAnalytics({
                    realm: json.realm,
                    domain: json.domain,
                    activeBuildingCode: json.activeAccountName,
                    accountId: decoded?.context?.accountId,
                    userId: decoded?.context?.userId,
                    id: alias,
                    userName: decoded?.context?.userName,
                    userType: decoded?.context?.userType,
                    yard: decoded?.context?.yard,
                  });

                  pendoInitializedRef.current = true;
                  // Track initial screen size as an event
                  if (window.pendo) {
                    window.pendo.track("initial_screen_size", {
                      width: screenWidth,
                      height: screenHeight,
                      screenResolution,
                    });
                  }
                } catch (pendoError) {
                  console.warn("Error initializing Pendo (caught in inner try-catch):", pendoError);
                }
              })
              .catch((pendoError) => {
                console.warn("Error initializing Pendo (caught in Promise):", pendoError);
              });
          } catch (error) {
            rumService.error(
              "Error initializing Pendo (caught in outer try-catch):",
              error as Error
            );
          }

          rumService.setSessionMetadata({
            ...decoded?.context,
          });

          rumService.customEvent({
            type: RUM_EVENT_TYPES.SYSTEM_INIT,
            message: "Application initialized",
          });
          isInitializedRef.current = true;
        }
      } catch (error) {
        console.error("Error handling message:", error);
        setError("An error occurred while processing the authentication data.");
        rumService.error("Authentication Error", error as Error);
      }
    };

    const handleError = (errorEvent: ErrorEvent) => {
      rumService.error("Global Error", errorEvent);
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("error", handleError);
    window.parent.postMessage("Component mounted", "*");

    // Dev environment setup
    if (process.env.NODE_ENV === "development") {
      const decoded = jwtDecode<TokenContext>(process.env.REACT_APP_SECURITY_TOKEN as string);
      const mapApiKeysString = mapApiKeys?.hereApiKey
        ? `{"hereApiKey":"${mapApiKeys.hereApiKey}"}`
        : process.env.REACT_APP_MAP_API_KEYS;

      const devAuthInfo = {
        securityToken: process.env.REACT_APP_SECURITY_TOKEN,
        realm: process.env.REACT_APP_REALM,
        domain: process.env.REACT_APP_DOMAIN,
        activeAccountName: process.env.REACT_APP_ACTIVE_ACCOUNT_NAME,
        mapApiKeys: mapApiKeysString,
        context: decoded?.context,
      };
      window.parent.postMessage(JSON.stringify(devAuthInfo), "*");
    }

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("error", handleError);
    };
  }, [dispatch]);

  // Handle feature flags
  useEffect(() => {
    const loadFeatureFlags = async () => {
      if (activeBuildingCode) {
        setIsFeatureFlagsLoading(true);
        try {
          // One batched IsFeatureEnabled call for the whole set (was one call per flag).
          await dispatch(
            fetchAllFeatureFlags(Object.values(FEATURE_FLAGS), Object.values(YARD_FEATURES))
          );
          // Fetch user permissions from BFF (Chloe RBAC check)
          await dispatch(fetchPermissions());
        } finally {
          setIsFeatureFlagsLoading(false);
        }
      }
    };
    loadFeatureFlags();
  }, [activeBuildingCode, dispatch]);

  const handleSetCurrentTab = (newTab: string) => {
    setCurrentTab(newTab);
    navigate(VIEW_TO_ROUTE[newTab]);
  };

  useEffect(() => {
    const hasPathChanged = prevPathRef.current !== location.pathname;
    const hasSearchChanged = prevSearchParamsRef.current !== location.search;
    const hasHashChanged = prevHashRef.current !== location.hash;

    if (hasPathChanged || hasSearchChanged || hasHashChanged) {
      const searchParams = new URLSearchParams(location.search);
      const paramChanges = {
        added: {} as Record<string, string>,
        removed: {} as Record<string, string>,
        modified: {} as Record<string, { from: string; to: string }>,
      };

      // Track parameter changes
      if (hasSearchChanged) {
        const oldParams = new URLSearchParams(prevSearchParamsRef.current);

        // Check for added and modified params
        searchParams.forEach((value, key) => {
          if (!oldParams.has(key)) {
            paramChanges.added[key] = value;
          } else if (oldParams.get(key) !== value) {
            paramChanges.modified[key] = {
              from: oldParams.get(key) || "",
              to: value,
            };
          }
        });

        // Check for removed params
        oldParams.forEach((value, key) => {
          if (!searchParams.has(key)) {
            paramChanges.removed[key] = value;
          }
        });
      }

      rumService.recordRouteChange(location.pathname, prevPathRef.current, {
        urlParamChanges: paramChanges,
        fullUrl: window.location.href,
        searchParamsChanged: hasSearchChanged,
        hashChanged: hasHashChanged,
        previousSearch: prevSearchParamsRef.current,
        currentSearch: location.search,
        previousHash: prevHashRef.current,
        currentHash: location.hash,
      });

      prevPathRef.current = location.pathname;
      prevSearchParamsRef.current = location.search;
      prevHashRef.current = location.hash;
    }
  }, [location]);

  return {
    currentTab,
    handleSetCurrentTab,
    error,
    activeBuildingCode,
    isLoading: !activeBuildingCode || isFeatureFlagsLoading,
    featureFlags,
  };
};
